import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Tag, Space, Input, Select, Modal, Form, message, Popconfirm, Descriptions } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, CheckCircleOutlined, CloseCircleOutlined, ArrowDownOutlined, ArrowUpOutlined } from '@ant-design/icons';
import './AdminCommon.css';

const { Option } = Select;
const { TextArea } = Input;

const SkillManagement = () => {
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [modalVisible, setModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [editingSkill, setEditingSkill] = useState(null);
  const [viewingSkill, setViewingSkill] = useState(null);
  const [form] = Form.useForm();

  // 获取技能列表
  const fetchSkills = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/admin/skills', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        const formattedSkills = (data.data || []).map(skill => ({
          id: skill.id,
          name: skill.displayName || skill.name,
          category: skill.category,
          status: skill.status === 1 ? 'active' : skill.status === 0 ? 'pending' : 'inactive',
          downloads: skill.downloadCount || 0,
          rating: skill.rating || 0,
          author: skill.authorName || '未知',
          description: skill.description,
          version: skill.version,
          tags: skill.tags || [],
          rawData: skill,
        }));
        setSkills(formattedSkills);
      } else {
        message.error(data.message || '获取技能列表失败');
      }
    } catch (error) {
      console.error('获取技能列表失败:', error);
      message.error('获取技能列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSkills();
  }, []);

  // 处理搜索和筛选
  const filteredSkills = skills.filter(skill => {
    const matchSearch = !searchText ||
      (skill.name && skill.name.toLowerCase().includes(searchText.toLowerCase())) ||
      (skill.author && skill.author.toLowerCase().includes(searchText.toLowerCase()));
    const matchStatus = statusFilter === 'all' || skill.status === statusFilter;
    return matchSearch && matchStatus;
  });

  // 查看技能详情
  const handleView = (record) => {
    setViewingSkill(record);
    setViewModalVisible(true);
  };

  // 打开新增弹窗
  const handleAdd = () => {
    setEditingSkill(null);
    form.resetFields();
    setModalVisible(true);
  };

  // 打开编辑弹窗
  const handleEdit = (record) => {
    setEditingSkill(record);
    form.setFieldsValue({
      name: record.rawData?.name || '',
      display_name: record.name,
      category: record.category,
      description: record.description || '',
      version: record.version || '',
      status: record.rawData?.status ?? 1,
      tags: Array.isArray(record.tags) ? record.tags.join(',') : (record.tags || ''),
    });
    setModalVisible(true);
  };

  // 删除技能
  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/admin/skills/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        message.success('删除成功');
        fetchSkills();
      } else {
        message.error(data.message || '删除失败');
      }
    } catch (error) {
      console.error('删除技能失败:', error);
      message.error('删除失败');
    }
  };

  // 审核 / 状态变更（approve / reject / offline / online）
  const handleStatusChange = async (id, action) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/admin/skills/${id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ action }),
      });
      const data = await res.json();
      if (data.success) {
        message.success(data.message || '操作成功');
        fetchSkills();
      } else {
        message.error(data.message || '操作失败');
      }
    } catch (error) {
      console.error('审核操作失败:', error);
      message.error('操作失败');
    }
  };

  // 提交表单（新增/编辑）
  const handleSubmit = async (values) => {
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...values,
        tags: values.tags ? values.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
      };

      if (editingSkill) {
        const res = await fetch(`/api/admin/skills/${editingSkill.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });
        const data = await res.json();
        if (data.success) {
          message.success('更新成功');
          setModalVisible(false);
          fetchSkills();
        } else {
          message.error(data.message || '更新失败');
        }
      } else {
        const res = await fetch('/api/admin/skills', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });
        const data = await res.json();
        if (data.success) {
          message.success('创建成功');
          setModalVisible(false);
          fetchSkills();
        } else {
          message.error(data.message || '创建失败');
        }
      }
    } catch (error) {
      console.error('保存技能失败:', error);
      message.error('操作失败');
    }
  };

  const categoryMap = {
    creation: '内容创作',
    distribution: '发行',
    marketing: '营销',
    opc: 'OPC',
    business: '商业服务',
    news: '信息资讯',
    data: '数据',
    general: '通用',
    other: '其他'
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: '技能名称', dataIndex: 'name', key: 'name' },
    {
      title: '分类',
      dataIndex: 'category',
      key: 'category',
      render: (cat) => categoryMap[cat] || cat,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : status === 'pending' ? 'orange' : 'red'}>
          {status === 'active' ? '已上架' : status === 'pending' ? '待审核' : '已下架'}
        </Tag>
      )
    },
    { title: '下载量', dataIndex: 'downloads', key: 'downloads', sorter: (a, b) => a.downloads - b.downloads },
    { title: '评分', dataIndex: 'rating', key: 'rating', sorter: (a, b) => a.rating - b.rating },
    { title: '作者', dataIndex: 'author', key: 'author' },
    {
      title: '操作',
      key: 'action',
      width: 360,
      render: (_, record) => (
        <Space size="small" wrap>
          <Button icon={<EyeOutlined />} size="small" onClick={() => handleView(record)}>
            查看
          </Button>
          <Button icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)}>
            编辑
          </Button>

          {/* 待审核：通过 / 拒绝 */}
          {record.status === 'pending' && (
            <>
              <Popconfirm
                title="确认通过此技能审核?"
                description="通过后将公开展示给所有用户"
                onConfirm={() => handleStatusChange(record.id, 'approve')}
                okText="通过"
                cancelText="取消"
              >
                <Button icon={<CheckCircleOutlined />} size="small" type="primary">
                  通过
                </Button>
              </Popconfirm>
              <Popconfirm
                title="确认拒绝此技能?"
                description="拒绝后将变为已下架状态"
                onConfirm={() => handleStatusChange(record.id, 'reject')}
                okText="拒绝"
                cancelText="取消"
                okButtonProps={{ danger: true }}
              >
                <Button icon={<CloseCircleOutlined />} size="small" danger>
                  拒绝
                </Button>
              </Popconfirm>
            </>
          )}

          {/* 已上架：下架 */}
          {record.status === 'active' && (
            <Popconfirm
              title="确认下架该技能?"
              description="下架后用户将无法看到此技能"
              onConfirm={() => handleStatusChange(record.id, 'offline')}
              okText="下架"
              cancelText="取消"
              okButtonProps={{ danger: true }}
            >
              <Button icon={<ArrowDownOutlined />} size="small">
                下架
              </Button>
            </Popconfirm>
          )}

          {/* 已下架：重新上架 */}
          {record.status === 'inactive' && (
            <Popconfirm
              title="确认重新上架?"
              onConfirm={() => handleStatusChange(record.id, 'online')}
              okText="上架"
              cancelText="取消"
            >
              <Button icon={<ArrowUpOutlined />} size="small" type="primary">
                重新上架
              </Button>
            </Popconfirm>
          )}

          <Popconfirm
            title="确认删除该技能?"
            onConfirm={() => handleDelete(record.id)}
            okText="确认"
            cancelText="取消"
          >
            <Button icon={<DeleteOutlined />} size="small" danger>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="admin-page">
      <div className="page-header">
        <h1 className="page-title">技能管理</h1>
        <p className="page-subtitle">管理平台所有技能</p>
      </div>

      <Card>
        <div className="table-toolbar" style={{ marginBottom: 16 }}>
          <Space>
            <Input.Search
              placeholder="搜索技能"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: 120 }}
            >
              <Option value="all">全部状态</Option>
              <Option value="active">已上架</Option>
              <Option value="pending">待审核</Option>
              <Option value="inactive">已下架</Option>
            </Select>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              新增技能
            </Button>
          </Space>
        </div>
        <Table
          columns={columns}
          dataSource={filteredSkills}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      {/* 新增/编辑弹窗 */}
      <Modal
        title={editingSkill ? '编辑技能' : '新增技能'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        width={600}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            label="技能标识名"
            name="name"
            rules={[{ required: true, message: '请输入技能标识名' }]}
            extra="用于系统识别的唯一标识，如：audio-generator"
          >
            <Input disabled={!!editingSkill} placeholder="audio-generator" />
          </Form.Item>
          <Form.Item
            label="显示名称"
            name="display_name"
            rules={[{ required: true, message: '请输入显示名称' }]}
          >
            <Input placeholder="音频生成器" />
          </Form.Item>
          <Form.Item
            label="分类"
            name="category"
            rules={[{ required: true, message: '请选择分类' }]}
          >
            <Select placeholder="选择分类">
              {Object.entries(categoryMap).map(([code, name]) => (
                <Option key={code} value={code}>{name}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="描述" name="description">
            <TextArea rows={3} placeholder="技能描述" />
          </Form.Item>
          <Form.Item label="版本" name="version">
            <Input placeholder="1.0.0" />
          </Form.Item>
          <Form.Item label="状态" name="status" initialValue={1}>
            <Select>
              <Option value={1}>已上架</Option>
              <Option value={0}>待审核</Option>
              <Option value={2}>已下架</Option>
            </Select>
          </Form.Item>
          <Form.Item label="标签" name="tags" extra="多个标签用英文逗号分隔">
            <Input placeholder="AI, 音频, 创作" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 查看详情弹窗 */}
      <Modal
        title="技能详情"
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setViewModalVisible(false)}>
            关闭
          </Button>
        ]}
        width={700}
      >
        {viewingSkill && (
          <Descriptions bordered column={2}>
            <Descriptions.Item label="ID">{viewingSkill.id}</Descriptions.Item>
            <Descriptions.Item label="状态">
              <Tag color={viewingSkill.status === 'active' ? 'green' : viewingSkill.status === 'pending' ? 'orange' : 'red'}>
                {viewingSkill.status === 'active' ? '已上架' : viewingSkill.status === 'pending' ? '待审核' : '已下架'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="技能名称">{viewingSkill.name}</Descriptions.Item>
            <Descriptions.Item label="分类">{categoryMap[viewingSkill.category] || viewingSkill.category}</Descriptions.Item>
            <Descriptions.Item label="作者">{viewingSkill.author}</Descriptions.Item>
            <Descriptions.Item label="版本">{viewingSkill.version || '-'}</Descriptions.Item>
            <Descriptions.Item label="下载量">{viewingSkill.downloads}</Descriptions.Item>
            <Descriptions.Item label="评分">{viewingSkill.rating}</Descriptions.Item>
            <Descriptions.Item label="标签" span={2}>
              {(viewingSkill.tags || []).map(tag => (
                <Tag key={tag}>{tag}</Tag>
              ))}
            </Descriptions.Item>
            <Descriptions.Item label="描述" span={2}>
              {viewingSkill.description || '暂无描述'}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default SkillManagement;
